import { useAnalytics } from '../context/AnalyticsContext';
import {
	ComposedChart, Bar, Line,
	XAxis, YAxis, CartesianGrid, Tooltip,
	ResponsiveContainer, Cell,
} from 'recharts';

const CustomTooltip = ({ active, payload, label }) => {
	if (!active || !payload?.length) return null;
	return (
		<div style={S.tooltip}>
			<div style={S.tooltipLabel}>{label}</div>
			{payload.map((p, i) => (
				<div key={i} style={{ color: p.color, fontSize: 12, marginTop: 3 }}>
					{p.name}: <strong>{p.value?.toLocaleString('en-GB')}</strong>
				</div>
			))}
			<div style={S.tooltipHint}>Click bar to see hourly breakdown</div>
		</div>
	);
};

export default function DailyChart() {
	const { state, selectDate } = useAnalytics();
	const { daily, filteredDaily, filterType, selectedDate, loading, filterLoading } = state;

	// any active filter (module, user or role) uses filteredDaily
	const isFiltered = !!filterType;
	const source     = isFiltered ? filteredDaily : daily;
	const busy       = isFiltered ? filterLoading : loading;

	if (busy) return <div style={S.empty}>Loading…</div>;
	if (!source?.length) return <div style={S.empty}>No data for the selected period</div>;

	const data = source.map(d => {
		const iso = typeof d.day === 'string' ? d.day.slice(0, 10) : new Date(d.day).toISOString().slice(0, 10);
		return {
			iso,
			label:   new Date(d.day).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
			'Views':  d.totalViews,
			'Unique': d.uniqueUsers,
		};
	});

	const handleBarClick = (barEntry) => {
		if (barEntry?.iso) selectDate(barEntry.iso);
	};

	return (
		<ResponsiveContainer width="100%" height={220}>
			<ComposedChart data={data} barGap={2} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
				<CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
				<XAxis
					dataKey="label"
					tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Outfit, sans-serif' }}
					axisLine={false} tickLine={false}
					interval="preserveStartEnd"
				/>
				<YAxis
					tick={{ fontSize: 11, fill: '#94A3B8', fontFamily: 'Outfit, sans-serif' }}
					axisLine={false} tickLine={false}
				/>
				<Tooltip content={<CustomTooltip />} cursor={{ fill: '#F8FAFC' }} />
				<Bar dataKey="Views" radius={[4, 4, 0, 0]} maxBarSize={36} onClick={handleBarClick} style={{ cursor: 'pointer' }}>
					{data.map((d, i) => {
						const isSelected = selectedDate === d.iso;
						return (
							<Cell
								key={i}
								fill={isSelected ? '#1D4ED8' : '#2563EB'}
								fillOpacity={isSelected ? 1 : selectedDate ? 0.35 : 0.65 + (i / data.length) * 0.35}
								stroke={isSelected ? '#1E40AF' : 'none'}
								strokeWidth={isSelected ? 2 : 0}
							/>
						);
					})}
				</Bar>
				<Line type="monotone" dataKey="Unique" stroke="#10B981" dot={{ r: 3, fill: '#10B981', strokeWidth: 0 }} strokeWidth={2.5} activeDot={{ r: 5 }} />
			</ComposedChart>
		</ResponsiveContainer>
	);
}

const S = {
	empty:        { color: '#94A3B8', textAlign: 'center', padding: '40px 0', fontSize: 13 },
	tooltip:      { background: '#fff', border: '1px solid #E4E9F0', borderRadius: 10, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' },
	tooltipLabel: { color: '#64748B', fontSize: 11, fontWeight: 600, marginBottom: 2 },
	tooltipHint:  { color: '#94A3B8', fontSize: 10, marginTop: 6, fontStyle: 'italic' },
};